import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RouteComponentProps } from 'react-router-dom';
import { Skeleton } from 'primereact/skeleton';
import styled from 'styled-components';
import FontIcon from 'components/icons/FontIcon';
import { TextButton } from 'components/v2/buttons';
import TitleAndBack from 'components/v2/TitleAndBack';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import EpdRoutes from 'routes/EpdRoutes';
import {
  getDocumentVersion,
  getVersionComparisonSchema,
  getDocumentDataSources,
  getDocumentComparison,
  getPropertiesSchema
} from 'services/api/dynamicFormsApi';
import { QUERY_KEYS } from 'services/api/constants';
import { Container } from 'styles/Styles.styled';
import { WizardHeaderGrid } from 'styles/v2/Styles.styled';
import { DataSources } from 'types/dynamic-forms/DataSources';
import { VersionLogRootRecord } from '../version-log/records/VersionLogRootRecord';
import ComparisonRenderer from './ComparisonRenderer';
import { ComparisonContext } from './hooks/useComparisonContext';
import { DocumentProperty } from 'types/dynamic-forms/DocumentProperty';

interface Params {
  docId: string;
  versionAId: string;
  versionBId: string;
}

const DynamicFormVersionComparison: React.FC<RouteComponentProps<Params>> = ({
  match: {
    params: { docId, versionAId, versionBId },
  },
  history,
}) => {
  const [fullWidth, setFullWidth] = useState(false);

  const versionAQuery = useQuery({
    queryKey: ['DOC_VERSION', docId, versionAId],
    queryFn: () => getDocumentVersion(docId, versionAId),
    throwOnError: true,
  });

  const versionBQuery = useQuery({
    queryKey: ['DOC_VERSION', docId, versionBId],
    queryFn: () => getDocumentVersion(docId, versionBId),
    throwOnError: true,
  });

  const diffQuery = useQuery({
    queryKey: ['DOC_COMPARE', docId, versionAId, versionBId],
    queryFn: () => getDocumentComparison(docId, versionAId, versionBId),
    throwOnError: true,
  });

  // The two versions normally share a schema; use the reference version's layout for both columns.
  const schemaId = versionAQuery.data?.schemaId;
  const schemaIdB = versionBQuery.data?.schemaId;

  const layoutQuery = useQuery({
    queryKey: ['DOC_SCHEMA', 'VERSION_COMPARISON', schemaId],
    queryFn: () => getVersionComparisonSchema(schemaId!) as Promise<VersionLogRootRecord>,
    enabled: !!schemaId,
    throwOnError: true,
  });

  const dataSourcesAQuery = useQuery({
    queryKey: QUERY_KEYS.DYNAMIC_FORMS_DATA_SOURCES(docId, schemaId, versionAId),
    queryFn: () => getDocumentDataSources(schemaId!, docId, versionAId) as Promise<DataSources>,
    enabled: !!schemaId,
    throwOnError: true,
  });

  const dataSourcesBQuery = useQuery({
    queryKey: QUERY_KEYS.DYNAMIC_FORMS_DATA_SOURCES(docId, schemaIdB, versionBId),
    queryFn: () => getDocumentDataSources(schemaIdB!, docId, versionBId) as Promise<DataSources>,
    enabled: !!schemaIdB,
    throwOnError: true,
  });

  const propertiesAQuery = useQuery({
    queryKey: ['DOC_SCHEMA', 'PROPERTIES', schemaId],
    queryFn: () => getPropertiesSchema(schemaId!) as Promise<DocumentProperty[]>,
    enabled: !!schemaId,
    throwOnError: true,
  });

  const propertiesBQuery = useQuery({
    queryKey: ['DOC_SCHEMA', 'PROPERTIES', schemaIdB],
    queryFn: () => getPropertiesSchema(schemaIdB!) as Promise<DocumentProperty[]>,
    enabled: !!schemaIdB,
    throwOnError: true,
  });

  const isReady =
    versionAQuery.data &&
    versionBQuery.data &&
    diffQuery.data &&
    layoutQuery.data &&
    dataSourcesAQuery.data &&
    dataSourcesBQuery.data;

  if (!isReady) {
    return (
      <Container>
        <Skeleton width="100%" height="4rem" className="mb-2" />
        <Skeleton width="100%" height="12rem" className="mb-2" />
        <Skeleton width="100%" height="12rem" />
      </Container>
    );
  }

  const versionA = versionAQuery.data!;
  const versionB = versionBQuery.data!;

  return (
    <Container $fullWidth={fullWidth}>
      <WizardHeaderGrid>
        <TitleAndBack
          title="EPD Version comparison"
          backTo="Back to the EPD"
          onBack={() => history.push(EpdRoutes.param(EpdRoutes.dynamicForm, ':docId', docId))}
        />
        <div>
          <TextButton
            className="white-space-nowrap"
            style={{ cursor: 'pointer' }}
            tooltip={fullWidth ? 'Condense view' : 'Expand view'}
            tooltipOptions={{ position: 'left' }}
            icon={<FontIcon icon="expand" />}
            text
            onClick={() => setFullWidth((v) => !v)}
          />
        </div>
      </WizardHeaderGrid>

      <HeaderRow>
        <ColumnHeader>{versionA.metadata.fullIdentificationNumber ?? versionA.metadata.title ?? ''}</ColumnHeader>
        <ColumnHeader>{versionB.metadata.fullIdentificationNumber ?? versionB.metadata.title ?? ''}</ColumnHeader>
      </HeaderRow>

      <ComparisonContext.Provider
        value={{
          left: { data: versionA.data, metadata: versionA.metadata, dataSources: dataSourcesAQuery.data!, properties: propertiesAQuery.data! },
          right: { data: versionB.data, metadata: versionB.metadata, dataSources: dataSourcesBQuery.data!, properties: propertiesBQuery.data! },
          diff: diffQuery.data!,
          // Overridden per card by FieldPanelRenderer; this outer value is only used to pass data down.
          side: 'left',
        }}
      >
        <ComparisonRenderer schema={layoutQuery.data} />
      </ComparisonContext.Provider>
    </Container>
  );
};

const HeaderRow = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;
  margin-bottom: 8px;
`;

const ColumnHeader = styled.div`
  ${(props) => props.theme.typography.headerH4}
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export default withGreyBackground(DynamicFormVersionComparison);
